import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconX } from '@tabler/icons-react';
import { TeeButton } from '@/components/ui/tee-button';
import { useInvalidateIssueNotices, useIssueNoticesLive } from '@/hooks/useIssueNotices';
import { issueNoticeService } from '@/services/issue-notice.service';
import { showErrorToast, showSuccessToast } from '@/utils/toast.utils';
import { getErrorMessage } from '@/utils/error';
import type { IssueNoticeAlert } from '@/types/issue-notice';
import { IssueSeverityBadge } from './IssueSeverityBadge';

/** Alerts older than this on arrival are not replayed (missed while offline). */
const REPLAY_WINDOW_MS = 2 * 60_000;
/** The banner already shows these (appearing, or the UI-7 "ended" toast). */
const SILENT_EVENTS = new Set(['published', 'resolved', 'expired']);
const SHOWN_KEY = 'issue-alerts-shown';
const SHOWN_MAX = 200;

/**
 * Alerts already toasted in this browser; storage may be unavailable.
 */
function loadShown(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(SHOWN_KEY) ?? '[]') as string[]);
  } catch {
    return new Set();
  }
}
function saveShown(ids: Set<string>) {
  try {
    localStorage.setItem(SHOWN_KEY, JSON.stringify([...ids].slice(-SHOWN_MAX)));
  } catch {
    // Without storage an alert can toast again after a reload; harmless.
  }
}

/**
 * Delivers in-app alerts from the live poll:
 * - actionable expiry warnings (owner / platform admins) stay on screen with
 *   one-tap Extend and Resolve until dismissed (TB-7);
 * - everything else shows once as a toast.
 * Shown alerts are remembered in this browser so none pops up twice.
 */
export function IssueAlertCards() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const invalidate = useInvalidateIssueNotices();
  const { data, serverOffsetMs } = useIssueNoticesLive();
  const handled = useRef<Set<string>>(loadShown());
  const [cards, setCards] = useState<IssueNoticeAlert[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    if (!data?.unreadAlerts.length) return;
    const now = Date.now() + serverOffsetMs;
    const newCards: IssueNoticeAlert[] = [];
    let changed = false;

    for (const alert of data.unreadAlerts) {
      if (handled.current.has(alert.id)) continue;
      handled.current.add(alert.id);
      changed = true;
      const fresh = now - Date.parse(alert.createdAt) < REPLAY_WINDOW_MS;
      if (fresh && alert.actionable) {
        newCards.push(alert);
        continue;
      }
      if (fresh && !SILENT_EVENTS.has(alert.eventType)) showSuccessToast(alert.title, 5000);
    }

    if (changed) saveShown(handled.current);
    if (newCards.length) setCards((c) => [...newCards, ...c].slice(0, 3));
  }, [data, serverOffsetMs]);

  const dismiss = (alert: IssueNoticeAlert) => {
    setCards((c) => c.filter((a) => a.id !== alert.id));
    void issueNoticeService.markAlertsRead([alert.id]).then(invalidate).catch(() => undefined);
  };

  const extend = async (alert: IssueNoticeAlert, minutes: number) => {
    setBusy(alert.id);
    try {
      await issueNoticeService.extend(alert.noticeId, { extendByMinutes: minutes });
      showSuccessToast(t('issues.toast.extended'));
      dismiss(alert);
      invalidate();
    } catch (err) {
      showErrorToast(getErrorMessage(err, t('issues.errors.generic')));
    } finally {
      setBusy(null);
    }
  };

  if (!cards.length) return null;

  return (
    <div className="fixed bottom-4 right-4 left-4 sm:left-auto z-[70] flex flex-col gap-2 sm:w-[380px]" aria-live="polite">
      {cards.map((alert) => (
        <div
          key={alert.id}
          role="alertdialog"
          aria-label={alert.title}
          className="rounded-[8px] border border-[var(--border-default)] bg-[var(--background-card)] p-4 shadow-lg"
        >
          <div className="flex items-start gap-2">
            <IssueSeverityBadge severity={alert.severity} className="shrink-0" />
            <p className="min-w-0 flex-1 text-[14px] font-semibold text-[var(--text-content-title)]">{alert.title}</p>
            <TeeButton
              variant="ghost"
              size="s"
              aria-label={t('issues.dismiss')}
              onClick={() => dismiss(alert)}
              leftIcon={<IconX size={16} />}
            />
          </div>
          <p className="mt-1 text-[13px] text-[var(--text-content-sub-title)]">{alert.body}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <TeeButton size="s" disabled={busy === alert.id} onClick={() => extend(alert, 30)} text={t('issues.extendBy', { label: '30 min' })} />
            <TeeButton size="s" variant="secondary" disabled={busy === alert.id} onClick={() => extend(alert, 60)} text={t('issues.extendBy', { label: '1 h' })} />
            <TeeButton
              size="s"
              variant="secondary"
              onClick={() => {
                dismiss(alert);
                navigate(`/issues/${alert.noticeId}?action=resolve`);
              }}
              text={t('issues.resolve')}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
